import { useAuthStore } from '../../auth/store/useAuthStore';
import type { AccountSetupState } from '../../auth/domain/accountSetup';
import { getOfflineAccountSnapshot } from '../../shell/session/offlineAccountCache';
import { getOrganizationBusinessProfileOffline } from '../../shell/connectivity/businessProfileCache';
import { useBusinessProfileStore } from './store/useBusinessProfileStore';
import {
  awaitSessionBootstrap,
  clearSessionBootstrapGate,
} from './sessionBootstrapGate';
import { clearBootstrapSessionCache } from './bootstrapMobileSessionService';
import {
  clearAccountSetupCache,
  getAccountSetupStateForUser,
  getRequiresOwnerOnboardingForUser,
  primeAccountSetupFromBootstrap,
} from './accountSetupCache';

export {
  clearAccountSetupCache,
  getAccountSetupStateForUser,
  primeAccountSetupFromBootstrap,
} from './accountSetupCache';

/** `null` = aún no resuelto; `true`/`false` = gate de onboarding. */
export function getRequiresOwnerOnboarding(): boolean | null {
  const uid = useAuthStore.getState().session?.user?.id;
  return getRequiresOwnerOnboardingForUser(uid);
}

export { canRunOperationalBackgroundWork } from './operationalBackgroundWork';

export async function primeBusinessProfileFromOfflineSnapshot(): Promise<boolean> {
  try {
    const profile = await getOrganizationBusinessProfileOffline();
    if (!profile) return false;
    useBusinessProfileStore.getState().setProfile(profile);
    return true;
  } catch {
    return false;
  }
}

export async function primeAccountSetupFromOfflineSnapshot(userId: string): Promise<boolean> {
  try {
    const snap = await getOfflineAccountSnapshot(userId);
    if (typeof snap?.requiresOwnerOnboarding !== 'boolean') return false;
    primeAccountSetupFromBootstrap(userId, {
      requiresOwnerOnboarding: snap.requiresOwnerOnboarding,
      ownerSetupCompletedAt: snap.ownerSetupCompletedAt ?? null,
      organizationId: snap.accountSetupOrganizationId ?? snap.entitlements?.organization_id ?? null,
      branchCount: snap.accountSetupBranchCount ?? snap.branches?.length ?? 0,
      organizationClosedAt: snap.accountSetupOrganizationClosedAt ?? null,
      pendingEmployeeInvitation: snap.pendingEmployeeInvitation ?? null,
      awaitingEmployeeInvitation: snap.awaitingEmployeeInvitation === true,
    });
    await primeBusinessProfileFromOfflineSnapshot();
    return true;
  } catch {
    return false;
  }
}

export type RefreshAccountSetupOptions = {
  force?: boolean;
};

/** Re-bootstrap: invalida caches y vuelve a leer account_setup del servidor. */
export async function refreshAccountSetup(options?: RefreshAccountSetupOptions): Promise<AccountSetupState | null> {
  const uid = useAuthStore.getState().session?.user?.id;
  if (!uid) {
    clearAccountSetupCache();
    return null;
  }

  if (options?.force === true) {
    clearBootstrapSessionCache();
    clearSessionBootstrapGate();
  }

  await awaitSessionBootstrap(uid, options?.force === true ? { force: true } : undefined);
  return getAccountSetupStateForUser(uid);
}

/**
 * Cold start: bootstrap de red primero (fuente de verdad).
 * Snapshot SQLite en paralelo — no bloquea si el RPC ya trae account_setup
 * (antes esperábamos migraciones ~700ms con el bootstrap ya listo).
 */
export async function resolveAccountSetupForSession(userId: string): Promise<AccountSetupState | null> {
  const offlinePrime = Promise.all([
    primeAccountSetupFromOfflineSnapshot(userId),
    primeBusinessProfileFromOfflineSnapshot(),
  ]).catch(() => [false, false] as const);

  const payload = await awaitSessionBootstrap(userId);
  if (!payload?.account_setup) {
    await offlinePrime;
  } else {
    void offlinePrime;
  }
  return getAccountSetupStateForUser(userId);
}
